// src/exercises/SpacyMessageRenderer.jsx
// ══════════════════════════════════════════════════════════════════════════════
// 🎨 RENDERER NEURONAL v2.0 — Renderizado inteligente de respuestas de Spacy
//    Maneja: **bold**, `code`, listas, fórmulas gramaticales, correcciones
//    Importar en SpeakingExercise.jsx y SpeakingExerciseFree.jsx
// ══════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { preProcess, parseBlocks } from './spacyMarkdown';

// ─────────────────────────────────────────────────────────────────────────────
// 2. DETECTORES DE PATRONES ESPECIALES
// ─────────────────────────────────────────────────────────────────────────────

// Detecta fórmulas gramaticales: "Subject + Verb + Object", "He/She/It + verb-s"
const GRAMMAR_FORMULA_RE =
    /\b(Subject|Object|Verb|Noun|Adjective|Adverb|Complement|Pronoun|Article|Determiner|Infinitive|Gerund|Participle|Auxiliary|Base form)\b.*?\+/i;

function isGrammarFormula(line) {
    return GRAMMAR_FORMULA_RE.test(line);
}

// Detecta etiquetas de sección: "Structure:", "Rule:", "Example:", "Note:"
const SECTION_LABEL_RE = /^(Structure|Rule|Formula|Pattern|Form|Positive|Negative|Question|Short Answer|Contraction|Use|Example|Note|Quick note|Tip|Reminder|Important|Warning)\s*:/i;

// Detecta correcciones estilo "❌ word → ✅ **correct**"  o  "'wrong' → **right**"
const CORRECTION_ARROW_RE = /['"]?([^'"→]+)['"]?\s*→\s*\*\*([^*]+)\*\*/;

// ─────────────────────────────────────────────────────────────────────────────
// 3. INLINE RENDERER — Parsea bold, code, quoted, correction arrows
// ─────────────────────────────────────────────────────────────────────────────
function InlineSegments({ text }) {
    if (!text) return null;

    // Regex combinado: **bold**, *italic*, _italic_, `code`, "quoted".
    //
    // El orden de las alternativas ES la precedencia: `**` tiene que intentarse
    // ANTES que `*`, o `**word**` se leería como cursiva vacía.
    //
    // La cursiva de un asterisco faltaba, y por eso Spacy escribía `*to be*` y
    // la burbuja pintaba literalmente los asteriscos. Es la marca que más usa
    // para citar un verbo en infinitivo, así que se veía en casi cada mensaje.
    // (Sin lookbehind a propósito: Safari sólo lo soporta desde la 16.4 y esto
    //  corre en el iPhone de un alumno, no en un banco de pruebas.)
    const tokenRE =
        /(\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|\b_([^_\n]+)_\b|`([^`\n]+)`|"([^"\n]{1,60})")/g;
    const parts = [];
    let last = 0;
    let m;

    while ((m = tokenRE.exec(text)) !== null) {
        // texto plano antes del token
        if (m.index > last) {
            parts.push(
                <span key={`t-${last}`}>{text.slice(last, m.index)}</span>
            );
        }

        if (m[2] !== undefined) {
            // **bold** → highlight amarillo
            parts.push(
                <mark key={`b-${m.index}`} className="spr-bold">
                    {m[2]}
                </mark>
            );
        } else if (m[3] !== undefined || m[4] !== undefined) {
            // *italic* / _italic_ → énfasis suave, sin robarle protagonismo
            // al amarillo del bold (que es el que marca lo que hay que aprender)
            parts.push(
                <em key={`i-${m.index}`} className="spr-italic">
                    {m[3] !== undefined ? m[3] : m[4]}
                </em>
            );
        } else if (m[5] !== undefined) {
            // `code` → monospace pill azul
            parts.push(
                <code key={`c-${m.index}`} className="spr-code">
                    {m[5]}
                </code>
            );
        } else if (m[6] !== undefined) {
            // "quoted" → cursiva verde
            parts.push(
                <em key={`q-${m.index}`} className="spr-quote">
                    &ldquo;{m[6]}&rdquo;
                </em>
            );
        }

        last = m.index + m[0].length;
    }

    // resto del texto
    if (last < text.length) {
        parts.push(<span key={`t-end`}>{text.slice(last)}</span>);
    }

    return <>{parts}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. GRAMMAR FORMULA RENDERER
//    "Subject + Verb + Object + Complement"
//    → chips de colores conectados por +
// ─────────────────────────────────────────────────────────────────────────────

const FORMULA_COLORS = {
    subject: '#6366f1',
    pronoun: '#6366f1',
    verb: '#22c55e',
    auxiliary: '#22c55e',
    infinitive: '#22c55e',
    gerund: '#22c55e',
    participle: '#22c55e',
    'base form': '#22c55e',
    object: '#f59e0b',
    complement: '#f59e0b',
    noun: '#f59e0b',
    adjective: '#fb7185',
    adverb: '#a78bfa',
    article: '#64748b',
    determiner: '#64748b',
    preposition: '#64748b',
};

function getChipColor(word) {
    const key = word.toLowerCase().replace(/[^a-z ]/g, '');
    return FORMULA_COLORS[key] || '#60a5fa';
}

function GrammarFormula({ line }) {
    // Separar la etiqueta del inicio si existe: "Structure: Subject + Verb"
    let label = null;
    let formula = line;

    const labelMatch = line.match(/^([^:]+):\s*(.*)/);
    if (labelMatch && labelMatch[1].length < 20) {
        label = labelMatch[1].trim();
        formula = labelMatch[2].trim();
    }

    // Dividir por "+" y "/" para obtener los chips
    // Cada segmento puede ser "Subject/Object" (alternativas) o "Verb-s/es" (variante)
    const segments = formula.split(/\s*\+\s*/);

    return (
        <div className="spr-formula-wrap">
            {label && <span className="spr-formula-label">{label}:</span>}
            <div className="spr-formula-chips">
                {segments.map((seg, i) => {
                    const parts = seg.trim().split(/\s*\/\s*/);
                    return (
                        <React.Fragment key={i}>
                            {i > 0 && <span className="spr-formula-plus">+</span>}
                            <span
                                className="spr-formula-chip"
                                style={{ '--chip-color': getChipColor(parts[0]) }}
                            >
                                {parts.map((p, pi) => (
                                    <React.Fragment key={pi}>
                                        {pi > 0 && <span className="spr-formula-slash">/</span>}
                                        <span>{p.trim()}</span>
                                    </React.Fragment>
                                ))}
                            </span>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. CORRECTION RENDERER
//    "'I have went' → **I have gone**"
// ─────────────────────────────────────────────────────────────────────────────
function CorrectionArrow({ line }) {
    const m = line.match(/^['"]?([^'"→\n]+?)['"]?\s*→\s*\*\*([^*\n]+)\*\*(.*)/);
    if (!m) return <InlineSegments text={line} />;

    return (
        <span className="spr-correction-row">
            <span className="spr-correction-wrong">{m[1].trim()}</span>
            <span className="spr-correction-arrow">→</span>
            <mark className="spr-bold spr-correction-right">{m[2].trim()}</mark>
            {m[3] && <InlineSegments text={m[3]} />}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. SECTION LABEL RENDERER
//    "Structure: ..." → badge + contenido
// ─────────────────────────────────────────────────────────────────────────────

const LABEL_COLORS = {
    structure: '#6366f1',
    formula: '#6366f1',
    pattern: '#6366f1',
    form: '#6366f1',
    rule: '#f59e0b',
    important: '#f59e0b',
    warning: '#ef4444',
    note: '#60a5fa',
    'quick note': '#60a5fa',
    tip: '#22c55e',
    reminder: '#22c55e',
    example: '#a78bfa',
    use: '#a78bfa',
    positive: '#22c55e',
    negative: '#ef4444',
    question: '#60a5fa',
    'short answer': '#f59e0b',
    contraction: '#fb7185',
};

function SectionLabel({ line }) {
    const m = line.match(/^([^:]+?)\s*:\s*(.*)/s);
    if (!m) return <InlineSegments text={line} />;

    const labelKey = m[1].toLowerCase().trim();
    const color = LABEL_COLORS[labelKey] || '#60a5fa';
    const content = m[2].trim();

    // Si el contenido es una fórmula gramatical, renderizarlo como tal
    if (content && isGrammarFormula(content)) {
        return (
            <div className="spr-section-line">
                <span className="spr-section-badge" style={{ '--badge-color': color }}>
                    {m[1].trim()}
                </span>
                <GrammarFormula line={content} />
            </div>
        );
    }

    return (
        <div className="spr-section-line">
            <span className="spr-section-badge" style={{ '--badge-color': color }}>
                {m[1].trim()}
            </span>
            <span className="spr-section-content">
                <InlineSegments text={content} />
            </span>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. SINGLE LINE RENDERER — Determina el tipo de cada línea
// ─────────────────────────────────────────────────────────────────────────────
function RenderLine({ line, index }) {
    if (!line.trim()) return null;

    // Detectar fórmula gramatical pura (sin etiqueta de sección)
    if (isGrammarFormula(line) && !SECTION_LABEL_RE.test(line)) {
        return <GrammarFormula key={index} line={line} />;
    }

    // Detectar etiqueta de sección
    if (SECTION_LABEL_RE.test(line)) {
        return <SectionLabel key={index} line={line} />;
    }

    // Detectar corrección con flecha
    if (CORRECTION_ARROW_RE.test(line)) {
        return <CorrectionArrow key={index} line={line} />;
    }

    // Texto normal con inline rendering
    return (
        <span key={index} className="spr-text-line">
            <InlineSegments text={line} />
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SpacyMessageRenderer({ text, className = '' }) {
    if (!text) return null;

    const clean = preProcess(text);
    const blocks = parseBlocks(clean);

    return (
        <div className={`spr-root ${className}`}>
            {blocks.map((block, bi) => {
                if (block.type === 'paragraph') {
                    return (
                        <p key={bi} className="spr-paragraph">
                            {block.lines.map((line, li) => (
                                <React.Fragment key={li}>
                                    {li > 0 && <br />}
                                    <RenderLine line={line} index={li} />
                                </React.Fragment>
                            ))}
                        </p>
                    );
                }

                if (block.type === 'numbered') {
                    return (
                        <ol key={bi} className="spr-list spr-list-numbered">
                            {block.items.map((item, ii) => (
                                <li key={ii} className="spr-list-item">
                                    <RenderLine line={item} index={ii} />
                                </li>
                            ))}
                        </ol>
                    );
                }

                if (block.type === 'bullet') {
                    return (
                        <ul key={bi} className="spr-list spr-list-bullet">
                            {block.items.map((item, ii) => (
                                <li key={ii} className="spr-list-item">
                                    <RenderLine line={item} index={ii} />
                                </li>
                            ))}
                        </ul>
                    );
                }

                return null;
            })}
        </div>
    );
}